import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import { ModeEnum } from '../services/generated/Api';

const HISTORY_PAGE_SIZE = 30;

export function useRushMidHistory(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rushMidHistory,
    queryFn: () => apiClient.api.rushMidControllerHistory({ take: HISTORY_PAGE_SIZE }),
    enabled,
    refetchOnMount: 'always',
  });
}

export type RushMidRatingMode = 1 | 2 | 5;

const MODE_ENUM: Record<RushMidRatingMode, ModeEnum> = {
  1: ModeEnum.Value1,
  2: ModeEnum.Value2,
  5: ModeEnum.Value5,
};

/** ELO rating + top-50 leaderboard for a mode (1/2/5). */
export function useRushMidRating(mode: RushMidRatingMode, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rushMidRating(mode),
    queryFn: () => apiClient.api.rushMidControllerRating({ mode: MODE_ENUM[mode] }),
    enabled,
    refetchOnMount: 'always',
  });
}

/** The user's saved fighter skin per side (+ weapon skins) — to prefill the lobby picker. */
export function useRushMidSkins(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rushMidSkins,
    queryFn: () => apiClient.api.rushMidControllerGetSkins(),
    enabled,
  });
}

/** Full detail + replay of one past match (for the history result/replay/log screen). */
export function useRushMidMatchDetail(matchId: string | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['rushMid', 'match', matchId] as const,
    queryFn: () => apiClient.api.rushMidControllerMatchDetail(matchId as string),
    enabled: !!matchId,
  });
}

/** A shared replay of any finished match — the deep-link viewer. Same shape as match-detail
 *  minus the personal fields; watchable by any signed-in user, not only participants. */
export function useRushMidReplay(matchId: string | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['rushMid', 'replay', matchId] as const,
    queryFn: () => apiClient.api.rushMidControllerReplay(matchId as string),
    enabled: !!matchId,
  });
}
